import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { afgeslotenWeken, isWeekSleutel } from '../week';

/** Bewaartermijn (EXPERIENCE.md M15): verder terug dan 104 weken kan de Weekkiezer niet. */
export const BEWAARTERMIJN_WEKEN = 104;

export interface GekozenWeek {
  /** De week die de weergave toont; leeg zolang er nog geen enkele week is afgesloten. */
  week: string;
  /** De weken die de kiezer mag aanbieden, nieuwste bovenaan. */
  weken: string[];
  kies: (week: string) => void;
}

/**
 * De gekozen week leeft in het adres (UX-DR16, `?week=2026-W35`).
 *
 * Niet in component-state: dit scherm wordt in gesprekken gedeeld, en een link die bij de ander een
 * andere week opent dan bij de afzender is erger dan geen link. Een week die niet bestaat of nog
 * niet is afgesloten wordt vervángen door de nieuwste afgesloten week — niet doorgegeven aan de
 * API, die er terecht een 400 op geeft, en niet stil genegeerd, want dan wijst het adres naar iets
 * anders dan het scherm toont.
 */
export function useGekozenWeek(): GekozenWeek {
  const [searchParams, setSearchParams] = useSearchParams();
  // Bewust niet in een `useMemo` met een lege afhankelijkheidslijst: die bevriest de lijst bij het
  // openen van het tabblad, en een tabblad dat over de maandagnacht heen openblijft biedt de zojuist
  // afgesloten week dan nooit aan. De lijst is goedkoop en wordt nergens als afhankelijkheid gebruikt.
  const weken = afgeslotenWeken(new Date()).slice(0, BEWAARTERMIJN_WEKEN);

  const uitAdres = searchParams.get('week');
  const bruikbaar = isWeekSleutel(uitAdres) && weken.includes(uitAdres!);
  const week = bruikbaar ? uitAdres! : (weken[0] ?? '');

  useEffect(() => {
    if (bruikbaar) return;
    const volgende = new URLSearchParams(searchParams);
    // Is er niets af te sluiten, dan verdwijnt de week uit het adres: een adres dat een week noemt
    // die het scherm niet toont, is een link die bij de ander iets anders opent.
    if (week === '') {
      if (!volgende.has('week')) return;
      volgende.delete('week');
    } else {
      volgende.set('week', week);
    }
    setSearchParams(volgende, { replace: true });
  }, [bruikbaar, week, searchParams, setSearchParams]);

  const kies = useCallback(
    (gekozen: string) => {
      const volgende = new URLSearchParams(searchParams);
      volgende.set('week', gekozen);
      setSearchParams(volgende);
    },
    [searchParams, setSearchParams],
  );

  return { week, weken, kies };
}
